/**
 * LogisticsDashboardPage — operations surface for the logistics team
 * (Phase 5l-c, Jun 11 2026). See docs/roles/ana_followup_workflow_2026-06-10.md §5l-c.
 *
 * Works the logistics_items queue (migration 112) auto-spawned when a
 * treatment proposal hits fully_signed, plus manually-added travel /
 * provider bookings. Three views over the same data:
 *   - By-clinic Kanban: pending → ordered → confirmed → delivered columns
 *     with one-click status advancement + cancel.
 *   - "My items" toggle: the signed-in user's assigned queue.
 *   - Upcoming deadlines: an agenda of needed-by dates (the practical
 *     "calendar" — overdue + next-30-day rollup).
 *
 * Talks to routes/logistics.js (list-by-clinic / list-assigned-to-me /
 * set-status) + operations-calendar:clinics for the clinic dropdown.
 * Permission: logistics:read to view, logistics:write to advance status
 * (logistics role; physician + patient_experience + sysadmin hold
 * cross-team read).
 *
 * PHI boundary: shows who/what/when/where (names, dates, clinic) — never
 * clinical PHI, matching the route's design.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Truck, Loader2, RefreshCcw, AlertCircle, MapPin, CalendarClock,
  Package, Plane, Stethoscope, MoreHorizontal, User as UserIcon,
} from "lucide-react";
import PanelLoadError from "../components/PanelLoadError";
import { logLoadError } from "../lib/loadError";

// Active lifecycle columns (cancelled is hidden from the board by default).
const COLUMNS = [
  { key: "pending",   label: "Pending",   color: "#7c5210", bg: "#fdf6e9" },
  { key: "ordered",   label: "Ordered",   color: "#1f4f96", bg: "#eef3fb" },
  { key: "confirmed", label: "Confirmed", color: "#6b1f8b", bg: "#f6eefa" },
  { key: "delivered", label: "Delivered", color: "#2d6b2d", bg: "#eef6ee" },
];
// Legal forward move per column (mirrors _logistics_state.TRANSITIONS).
const NEXT_STATUS = { pending: "ordered", ordered: "confirmed", confirmed: "delivered" };
const KIND_ICON = { product: Package, travel: Plane, provider: Stethoscope, other: MoreHorizontal };
const KIND_LABEL = { product: "Product", travel: "Travel", provider: "Provider", other: "Other" };

function hasPerm(user, p) { return new Set((user && user.permissions) || []).has(p); }

function fmtDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function daysUntil(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return Math.ceil((dt.getTime() - Date.now()) / 86400000);
}

export default function LogisticsDashboardPage({ user, onNavigate }) {
  const canRead = hasPerm(user, "logistics:read");
  const canWrite = hasPerm(user, "logistics:write");

  const [clinics, setClinics] = useState([]);
  const [clinicId, setClinicId] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /*
   * ⛔⛔ TWO PANELS, TWO FAILURES, TOLD APART — Card P114 PR-3.
   *
   * `error` above is for ACTIONS (advancing an item). These two are for
   * LOADS, and they are separate because the clinic list and the item list
   * fail independently: a working board with an unreachable clinic list is a
   * real state, and collapsing it into one banner hides which half is down.
   */
  const [itemsError, setItemsError] = useState(null);
  const [clinicsError, setClinicsError] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadAll = useCallback(async () => {
    if (!canRead) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    setItemsError(null);
    setClinicsError(null);
    const api = window.zignagenixAPI;

    /*
     * ⛔⛔ `.catch(() => [])` ON THE CLINIC LIST WAS THE WHOLE "All Clinics
     * cannot select" REPORT — Card P114 PR-3.
     *
     * Luisa, 23 Oct. An empty `clinics` table and a refused
     * `operations-calendar:clinics` produced the SAME dropdown: one option,
     * "All clinics", nothing to pick. The catch turned a failure into a clean
     * empty result (the `[]`-swallow shape this repo has been bitten by
     * before), so nobody could tell which it was — including from the
     * console, which said nothing.
     *
     * ⚠ `allSettled`, NOT `all`. The two loads are independent and one must
     * not take the other down: a reachable item list behind an unreachable
     * clinic list is a board staff can still work.
     */
    const [clinicRes, listRes] = await Promise.allSettled([
      api.invoke("operations-calendar:clinics"),
      mineOnly
        ? api.invoke("logistics:list-assigned-to-me", { statusGroup: "all", limit: 300 })
        : api.invoke("logistics:list-by-clinic", {
            clinicId: clinicId === "all" ? undefined : parseInt(clinicId, 10),
            limit: 300,
          }),
    ]);

    if (clinicRes.status === "fulfilled" && Array.isArray(clinicRes.value)) {
      setClinics(clinicRes.value);
    } else {
      const reason = clinicRes.status === "rejected" ? clinicRes.reason : clinicRes.value;
      logLoadError("the clinic list", reason);
      setClinics([]);
      setClinicsError(reason || { code: "unknown" });
    }

    if (listRes.status === "fulfilled" && Array.isArray(listRes.value)) {
      setItems(listRes.value);
    } else {
      const reason = listRes.status === "rejected" ? listRes.reason : listRes.value;
      logLoadError("logistics items", reason);
      setItems([]);
      setItemsError(reason || { code: "unknown" });
    }

    setLoading(false);
  }, [canRead, clinicId, mineOnly]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function advance(item, toStatus) {
    setBusy(item.id);
    setError(null);
    try {
      let cancellationReason;
      if (toStatus === "cancelled") {
        cancellationReason = window.prompt("Reason for cancelling this item? (required, logged)");
        if (!cancellationReason || !cancellationReason.trim()) { setBusy(null); return; }
      }
      const r = await window.zignagenixAPI.invoke("logistics:set-status", {
        id: item.id, status: toStatus, cancellationReason,
      });
      if (r && r.error) setError(r.message || r.code);
      else await loadAll();
    } catch (e) {
      setError((e && e.message) || "Status change failed");
    }
    setBusy(null);
  }

  // Board groups (exclude cancelled) + summary + upcoming deadlines.
  const board = useMemo(() => {
    const cols = { pending: [], ordered: [], confirmed: [], delivered: [] };
    for (const it of items) if (cols[it.status]) cols[it.status].push(it);
    return cols;
  }, [items]);

  const summary = useMemo(() => {
    const open = items.filter((i) => ["pending", "ordered", "confirmed"].includes(i.status));
    const overdue = open.filter((i) => { const d = daysUntil(i.needed_by); return d != null && d < 0; });
    const dueSoon = open.filter((i) => { const d = daysUntil(i.needed_by); return d != null && d >= 0 && d <= 7; });
    return { open: open.length, overdue: overdue.length, dueSoon: dueSoon.length, total: items.length };
  }, [items]);

  const upcoming = useMemo(() => {
    return items
      .filter((i) => ["pending", "ordered", "confirmed"].includes(i.status) && i.needed_by)
      .filter((i) => { const d = daysUntil(i.needed_by); return d != null && d <= 30; })
      .sort((a, b) => new Date(a.needed_by) - new Date(b.needed_by))
      .slice(0, 20);
  }, [items]);

  if (!canRead) {
    return <div style={{ padding: 24, color: "#5a6478" }}>You don't have access to the Logistics dashboard.</div>;
  }
  if (loading) return <div className="page-loading" style={{ padding: 24 }}>Loading Logistics…</div>;

  return (
    <div style={{ padding: "16px 24px", maxWidth: 1500, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Truck size={22} color="#2E8B98" />
          <div>
            <h1 style={{ fontSize: 21, margin: 0 }}>Logistics</h1>
            <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>
              Product orders, patient travel, and provider bookings across clinics.
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => setMineOnly((v) => !v)} style={mineOnly ? chipActive : chip}>
            <UserIcon size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
            My items
          </button>
          <select
            value={clinicId}
            onChange={(e) => setClinicId(e.target.value)}
            disabled={mineOnly}
            style={{ ...inp, width: "auto", minWidth: 160, opacity: mineOnly ? 0.5 : 1 }}
            title={mineOnly ? "Showing your assigned items across all clinics" : "Filter by clinic"}
          >
            <option value="all">All clinics</option>
            {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}{c.city ? ` · ${c.city}` : ""}</option>)}
          </select>
          <button onClick={loadAll} style={btnSecondary}><RefreshCcw size={13} /> Refresh</button>
        </div>
      </div>

      {error && (
        <div style={{ background: "#fde8e8", border: "1px solid #f5a5a5", padding: 10, borderRadius: 4, marginBottom: 12, color: "#8b0000", display: "flex", alignItems: "center", gap: 6 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Card P114 PR-3 — a load that FAILED says so, and offers the way back. */}
      {clinicsError && (
        <PanelLoadError what="the clinic list" error={clinicsError} onRetry={loadAll} />
      )}
      {itemsError && (
        <PanelLoadError what="logistics items" error={itemsError} onRetry={loadAll} />
      )}

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 18 }}>
        <Card label="Open items" value={summary.open} color="#1f6feb" />
        <Card label="Overdue" value={summary.overdue} color="#b00020" />
        <Card label="Due ≤ 7 days" value={summary.dueSoon} color="#7c5210" />
        <Card label={mineOnly ? "Assigned to me" : "Total shown"} value={summary.total} color="#5a6478" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 18, alignItems: "start" }}>
        {/* Kanban */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
          {COLUMNS.map((col) => (
            <div key={col.key} style={{ background: "#f7f9fc", border: "1px solid #e1e6ed", borderRadius: 6, padding: 8, minHeight: 200 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: col.color, textTransform: "uppercase", letterSpacing: "0.04em" }}>{col.label}</span>
                <span style={{ fontSize: 11, color: "#9aa4b2" }}>{board[col.key].length}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {board[col.key].length === 0 ? (
                  <div style={{ fontSize: 11, color: "#b5bdc9", fontStyle: "italic", padding: "6px 2px" }}>—</div>
                ) : board[col.key].map((it) => (
                  <LogisticsCard
                    key={it.id} item={it} canWrite={canWrite} busy={busy === it.id}
                    onAdvance={advance}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Upcoming deadlines agenda */}
        <div style={{ background: "#fff", border: "1px solid #e1e6ed", borderRadius: 6, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <CalendarClock size={15} color="#2E8B98" />
            <strong style={{ fontSize: 13 }}>Upcoming deadlines</strong>
          </div>
          {upcoming.length === 0 ? (
            <div style={{ fontSize: 12, color: "#9aa4b2", fontStyle: "italic" }}>Nothing due in the next 30 days.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {upcoming.map((it) => {
                const d = daysUntil(it.needed_by);
                const overdue = d != null && d < 0;
                return (
                  <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                    <span style={{ minWidth: 54, fontWeight: 700, color: overdue ? "#b00020" : "#3a4a5e" }}>
                      {fmtDate(it.needed_by)}
                    </span>
                    <span style={{ flex: 1, color: "#1F2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {it.title}
                    </span>
                    <span style={{ fontSize: 10, color: overdue ? "#b00020" : "#9aa4b2", fontWeight: overdue ? 700 : 400 }}>
                      {overdue ? `${Math.abs(d)}d late` : d === 0 ? "today" : `${d}d`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LogisticsCard({ item, canWrite, busy, onAdvance }) {
  const Icon = KIND_ICON[item.kind] || MoreHorizontal;
  const next = NEXT_STATUS[item.status];
  const d = daysUntil(item.needed_by);
  const overdue = d != null && d < 0 && item.status !== "delivered";
  const contactName = [item.contact_first_name, item.contact_last_name].filter(Boolean).join(" ");
  return (
    <div style={{ background: "#fff", border: `1px solid ${overdue ? "#f0b4b4" : "#e1e6ed"}`, borderRadius: 5, padding: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <Icon size={12} color="#5a6478" />
        <span style={{ fontSize: 10, color: "#5a6478", textTransform: "uppercase", letterSpacing: "0.03em" }}>
          {KIND_LABEL[item.kind] || item.kind}{item.subtype ? ` · ${item.subtype.replace(/_/g, " ")}` : ""}
        </span>
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1F2937", marginBottom: 3 }}>{item.title}</div>
      <div style={{ fontSize: 11, color: "#6C757D", display: "flex", flexWrap: "wrap", gap: 8 }}>
        {contactName && <span>{contactName}</span>}
        {item.clinic_name && <span><MapPin size={9} style={{ verticalAlign: "-1px" }} /> {item.clinic_name}</span>}
        {item.needed_by && (
          <span style={{ color: overdue ? "#b00020" : "#6C757D", fontWeight: overdue ? 700 : 400 }}>
            due {fmtDate(item.needed_by)}{overdue ? ` (${Math.abs(d)}d late)` : ""}
          </span>
        )}
      </div>
      {item.vendor_name && <div style={{ fontSize: 10.5, color: "#9aa4b2", marginTop: 2 }}>{item.vendor_name}{item.vendor_reference ? ` · ${item.vendor_reference}` : ""}</div>}
      {item.assigned_to_name && <div style={{ fontSize: 10.5, color: "#9aa4b2", marginTop: 2 }}>→ {item.assigned_to_name}</div>}

      {canWrite && item.status !== "delivered" && item.status !== "cancelled" && (
        <div style={{ display: "flex", gap: 6, marginTop: 7 }}>
          {next && (
            <button onClick={() => onAdvance(item, next)} disabled={busy} style={btnTiny}>
              {busy ? <Loader2 size={11} className="spin" /> : `→ ${next}`}
            </button>
          )}
          <button onClick={() => onAdvance(item, "cancelled")} disabled={busy} style={btnTinyGhost} title="Cancel item">✕</button>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e1e6ed", borderRadius: 4, padding: 14 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", color: "#5a6478", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: value > 0 ? color : "#5a6478", marginTop: 4 }}>{value}</div>
    </div>
  );
}

const inp = {
  padding: "6px 8px", fontSize: 12.5, color: "#1f2a3b",
  border: "1px solid #c9d6e8", borderRadius: 4, background: "#fff", boxSizing: "border-box", fontFamily: "inherit",
};
const btnSecondary = {
  padding: "6px 12px", fontSize: 12.5, fontWeight: 600, background: "#fff", color: "#3a4a5e",
  border: "1px solid #c9d6e8", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};
const chip = { ...btnSecondary, borderRadius: 14 };
const chipActive = { ...chip, background: "#2E8B98", color: "#fff", border: "1px solid #2E8B98" };
const btnTiny = {
  padding: "3px 8px", fontSize: 11, fontWeight: 600, background: "#2E8B98", color: "#fff",
  border: "none", borderRadius: 5, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, textTransform: "capitalize",
};
const btnTinyGhost = {
  padding: "3px 8px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#8b0000",
  border: "1px solid #f0b4b4", borderRadius: 5, cursor: "pointer",
};
